// src/pages/tasks/Home.tsx
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useTransition,
} from "react";
import {
  Paper,
  TablePagination,
  Avatar,
  Box,
  Tooltip,
  Button,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  FormControl,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Checkbox,
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Stack,
  FormGroup,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  useMediaQuery,
  Fade,
  Collapse,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme, alpha } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import TableViewIcon from "@mui/icons-material/TableView";
import GridViewIcon from "@mui/icons-material/GridView";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import LinkIcon from "@mui/icons-material/Link";
import BoltIcon from "@mui/icons-material/Bolt";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PersonIcon from "@mui/icons-material/Person";
import FilterListIcon from "@mui/icons-material/FilterList";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import TuneIcon from "@mui/icons-material/Tune";
import FileDownloadIcon from "@mui/icons-material/FileDownload"; // NEW (export)
import AutorenewIcon from "@mui/icons-material/Autorenew"; // NEW (auto refresh chip)
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DescriptionIcon from "@mui/icons-material/Description";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";

import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { baseUrl } from "../../constants";
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { TaskParent } from "../../enums/task-parent.enum";

import TasksGrid from "./TasksGrid";
import TasksTable from "./TasksTable";
import TasksCalendar from "./TasksCalendar";
import CommentDialog from "./CommentDialog";
import { getUserInfoFromToken } from "../../utils/Main";
import type { Task } from "../../utils/task";
import { InputBase } from "@mui/material";

/* ----------------------------- Local interfaces ---------------------------- */
interface User {
  id: number;
  username: string;
  imageUrl: string | null;
}
interface Project {
  id: number;
  name: string;
  departmentId?: number;
}
interface Department {
  id: number;
  name: string;
}

// --- Visibility options
const VISIBILITY_OPTIONS = [
  "PRIVATE",
  "PROJECT",
  "DEPARTMENT",
  "WORKSPACE",
] as const;

// --- helper: normalize project-users API shape to flat User
const toFlatUser = (raw: any): User | null => {
  if (!raw) return null;

  // already flat
  if (typeof raw.id === "number" && typeof raw.username === "string") {
    return {
      id: raw.id,
      username: raw.username,
      imageUrl: raw.imageUrl ?? raw.profile?.imageUrl ?? null,
    };
  }

  // API returns { user: {...}, projectId }
  if (raw.user && typeof raw.user.id === "number") {
    return {
      id: raw.user.id,
      username: raw.user.username ?? String(raw.user.id),
      imageUrl: raw.user.imageUrl ?? raw.user.profile?.imageUrl ?? null,
    };
  }

  return null;
};
/* ----------------------------- tiny debounce hook -------------------------- */
function useDebouncedValue<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* --------------------------------- Home ----------------------------------- */
const Home: React.FC = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const userInfo = getUserInfoFromToken() || {};
  const currentUserId = userInfo.id || 0;
  const currentUsername = userInfo.username || "";
  const isAdmin =
    (getUserInfoFromToken()?.role || "").toLowerCase() === "admin";

  const [activeCommentTaskId, setActiveCommentTaskId] = useState<number | null>(
    null
  );

  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // Focus search with Ctrl/⌘+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Data with enhanced state management
  const [rows, setRows] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();

  // Standard pagination (backend supports offset pagination)
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("limit")) || 50
  );
  const [totalRows, setTotalRows] = useState(0);

  // Density for grid/table
  const [density, setDensity] = useState<"comfortable" | "compact">(
    (localStorage.getItem("tasks:density") as "comfortable" | "compact") ||
      "comfortable"
  );

  // Save density to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("tasks:density", density);
    console.log(`💾 Density saved: ${density}`);
  }, [density]);

  // Filters drawer state
  const [filtersOpen, setFiltersOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem("tasks:filtersOpen");
    return saved === null ? true : saved === "true"; // Default to open
  });

  // Save filters drawer state whenever it changes
  useEffect(() => {
    localStorage.setItem("tasks:filtersOpen", String(filtersOpen));
    console.log(`💾 Filters drawer: ${filtersOpen ? "open" : "closed"}`);
  }, [filtersOpen]);

  // Auto refresh
  const [autoRefresh, setAutoRefresh] = useState<boolean>(
    localStorage.getItem("tasks:autoRefresh") === "true"
  );

  // Filter dialog
  const [filterOpen, setFilterOpen] = useState(false);

  // Load saved filters (NEW KEY)
  const saved = localStorage.getItem("Home_filter")
    ? JSON.parse(localStorage.getItem("Home_filter")!)
    : null;

  const parseDateParam = (key: string) =>
    searchParams.get(key)
      ? dayjs(searchParams.get(key)!)
      : saved?.[key]
      ? dayjs(saved[key])
      : null;

  const [startDateFrom, setStartDateFrom] = useState<Dayjs | null>(
    parseDateParam("startDateFrom")
  );
  const [startDateTo, setStartDateTo] = useState<Dayjs | null>(
    parseDateParam("startDateTo")
  );
  const [endDateFrom, setEndDateFrom] = useState<Dayjs | null>(
    parseDateParam("endDateFrom")
  );
  const [endDateTo, setEndDateTo] = useState<Dayjs | null>(
    parseDateParam("endDateTo")
  );

  // View
  const [viewMode, setViewMode] = useState<"table" | "grid" | "calendar">(() => {
    const saved = localStorage.getItem("tasks:view");
    return (saved === "table" || saved === "grid" || saved === "calendar") ? saved : "grid";
  });

  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("tasks:view", viewMode);
    console.log(`💾 View mode saved: ${viewMode}`);
  }, [viewMode]);

  // Status & Importance (multi, with "ALL")
  const initialStatusParam =
    searchParams.get("status") ||
    (saved?.status ? String(saved.status) : undefined);
  const initialImportance =
    searchParams.get("importance") ||
    (saved?.importance ? String(saved.importance) : undefined);

  const [status, setStatus] = useState<string[]>(
    initialStatusParam ? initialStatusParam.split(",") : [TaskStatus.ALL]
  );
  const [priority, setPriority] = useState<string[]>(
    initialImportance ? initialImportance.split(",") : [TaskImportance.ALL]
  );

  const [parentLevel, setParentLevel] = useState<string>(
    searchParams.get("parentLevel") || saved?.parentLevel || TaskParent.ALL
  );

  // Visibility (multi)
  const initialVisibilityParam =
    searchParams.get("visibility") ||
    (saved?.visibility ? String(saved.visibility) : undefined);
  const [visibility, setVisibility] = useState<string[]>(
    initialVisibilityParam ? initialVisibilityParam.split(",") : []
  );

  const [searchValue, setSearchValue] = useState<string>(
    searchParams.get("title") || saved?.title || ""
  );
  const debouncedSearch = useDebouncedValue(searchValue, 400);

  const [followed, setFollowed] = useState<boolean>(
    searchParams.get("follow") === "true" || saved?.follow || false
  );
  const [isPublic, setIsPublic] = useState<boolean>(
    searchParams.get("isPublic") === "true" || saved?.isPublic || false
  );

  // "Created by others" helper
  const [createdByOthers, setCreatedByOthers] = useState<boolean>(false);

  // admin: see-all toggle (persist + URL support)
  // Default to true for admins (see all tasks), false for non-admins (personal tasks only)
  const [adminSeeAll, setAdminSeeAll] = useState<boolean>(
    searchParams.get("adminSeeAll") === "true" || 
    (saved?.adminSeeAll !== undefined ? saved.adminSeeAll : isAdmin)
  );

  // Debug logging to verify admin status and filtering
  useEffect(() => {
    console.log('Task Filtering Debug:', {
      isAdmin,
      adminSeeAll,
      currentUserId,
      shouldSeeAllTasks: isAdmin && adminSeeAll,
      shouldSeePersonalTasks: !isAdmin || !adminSeeAll
    });
  }, [isAdmin, adminSeeAll, currentUserId]);

  // ---------- Departments + Projects (my-only logic) ----------
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<Department[]>(
    saved?.departmentIds &&
      Array.isArray(saved.departmentIds) &&
      saved.departmentNames
      ? (saved.departmentIds as number[]).map((id: number, idx: number) => ({
          id,
          name: saved.departmentNames[idx] || `#${id}`,
        }))
      : []
  );

  // My projects (source of truth)
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  // Projects to display in filter (depends on department selection)
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Project[]>(
    saved?.projectIds && Array.isArray(saved.projectIds) && saved.projectNames
      ? (saved.projectIds as number[]).map((id: number, idx: number) => ({
          id,
          name: saved.projectNames[idx] || `#${id}`,
        }))
      : []
  );

  // Users
  const [fetchingUsers, setFetchingUsers] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<User[]>(
    saved?.assignedUserIds && saved.assignedUsernames
      ? (saved.assignedUserIds as number[]).map((id: number, idx: number) => ({
          id,
          username: saved.assignedUsernames[idx] || `#${id}`,
          imageUrl: null,
        }))
      : []
  );
  const [selectedCreators, setSelectedCreators] = useState<User[]>(
    saved?.createdByIds && saved.createdByUsernames
      ? (saved.createdByIds as number[]).map((id: number, idx: number) => ({
          id,
          username: saved.createdByUsernames[idx] || `#${id}`,
          imageUrl: null,
        }))
      : []
  );

  // Dialog: change status with comment
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>();
  const [taskId, setTaskId] = useState<number>();
  const [changingStatus, setChangingStatus] = useState(false);
  const [statusChangeComment, setStatusChangeComment] = useState("");
  
  // Helper function to get userTask status display
  const getUserTaskStatusDisplay = (status: string) => {
    switch (status) {
      case "TODO":
        return { 
          label: "To Do", 
          icon: <HourglassBottomIcon fontSize="small" />, 
          color: theme.palette.grey[500] 
        };
      case "IN_PROGRESS":
        return { 
          label: "In Progress", 
          icon: <TimelapseIcon fontSize="small" />, 
          color: theme.palette.warning.main 
        };
      case "DONE":
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
      case "POSTPONED":
        return { 
          label: "Postponed", 
          icon: <DoNotDisturbOnIcon fontSize="small" />, 
          color: theme.palette.error.main 
        };
      default:
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
    }
  };

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  /* ------------------------------- Fetchers -------------------------------- */
  // keep selection synced: when ON, add all "others" but KEEP existing (incl. me)
  useEffect(() => {
    if (!createdByOthers) return;
    setSelectedCreators((prev) => {
      const ids = new Set(prev.map((u) => u.id));
      const merged = [...prev];
      users.forEach((u) => {
        if (u.id !== currentUserId && !ids.has(u.id)) merged.push(u);
      });
      return merged;
    });
  }, [users, createdByOthers, currentUserId]);

  // 1) Fetch my projects (I am assigned) once.
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const { data } = await axios.get(`${baseUrl}/projects/user/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const my = Array.isArray(data) ? data : [];
        setMyProjects(my);

        // 2) Build "my departments" from my projects
        const deptIds = Array.from(
          new Set(
            my
              .map((p: Project) => p.departmentId)
              .filter((x): x is number => typeof x === "number")
          )
        );

        // fetch all departments in workspace, then filter to my departmentIds
        const { data: allDepts } = await axios.get(
          `${baseUrl}/departments/workspace`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const onlyMine: Department[] = (allDepts || []).filter(
          (d: Department) => deptIds.includes(d.id)
        );

        setDepartments(onlyMine);

        // Initial projects display = my projects (no department filter yet)
        setProjects(my);
      } catch {
        setMyProjects([]);
        setDepartments([]);
        setProjects([]);
      }
    })();
  }, []);

  // 3) When departments selection changes, restrict the projects to myProjects within those departments
  useEffect(() => {
    if (!selectedDepartments.length) {
      setProjects(myProjects);
      // prune selected projects that are not in myProjects (safety)
      setSelectedProjects((prev) =>
        prev.filter((p) => myProjects.some((mp) => mp.id === p.id))
      );
      return;
    }
    const selectedDeptIds = new Set(selectedDepartments.map((d) => d.id));
    const filtered = myProjects.filter(
      (p) => p.departmentId && selectedDeptIds.has(p.departmentId)
    );
    setProjects(filtered);
    setSelectedProjects((prev) =>
      prev.filter((p) => filtered.some((fp) => fp.id === p.id))
    );
  }, [selectedDepartments, myProjects]);

  // Helper: merge users across project ids
  const fetchUsersByProjectIds = useCallback(
    async (ids: number[]) => {
      try {
        const token = localStorage.getItem("accessToken");
        setFetchingUsers(true);
        if (!ids.length) {
          setUsers([]);
          return;
        }
        const requests = ids.map((id) =>
          axios.get(`${baseUrl}/projects/project-users/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        const results = await Promise.allSettled(requests);
        const merged: Record<number, User> = {};
        results.forEach((r) => {
          if (r.status === "fulfilled") {
            const arr = Array.isArray(r.value.data) ? r.value.data : [];
            arr.forEach((row: any) => {
              const u = toFlatUser(row);
              if (u) merged[u.id] = merged[u.id] || u;
            });
          }
        });
        const list = Object.values(merged);
        setUsers(list);

        // prune selections not in merged list
        const ok = new Set(list.map((u) => u.id));
        setSelectedAssignees((prev) => prev.filter((u) => ok.has(u.id)));
        setSelectedCreators((prev) => prev.filter((u) => ok.has(u.id)));
      } catch {
        setUsers([]);
      } finally {
        setFetchingUsers(false);
      }
    },
    [setUsers]
  );
  // 4) Users union from selected projects (assignees & creators source list)
  // 4) Users union from selected projects (assignees & creators source list)
  useEffect(() => {
    const base = selectedProjects.length ? selectedProjects : myProjects;
    const ids = base.map((p) => p.id);
    fetchUsersByProjectIds(ids); // ← reuse the callback
  }, [selectedProjects, myProjects, fetchUsersByProjectIds]);

  /* -------------------------- Enhanced query object ------------------------- */
  const query = useMemo(() => {
    const q: any = {};

    if (debouncedSearch?.trim()) q.title = debouncedSearch.trim();

    // Normalize day range to whole-day bounds in UTC to avoid off-by-one
    if (startDateFrom)
      q.startDateFrom = startDateFrom.startOf("day").toISOString();
    if (startDateTo) q.startDateTo = startDateTo.endOf("day").toISOString();
    if (endDateFrom) q.endDateFrom = endDateFrom.startOf("day").toISOString();
    if (endDateTo) q.endDateTo = endDateTo.endOf("day").toISOString();

    if (isPublic) q.isPublic = true;
    // admin override – ALWAYS include adminSeeAll parameter for proper backend filtering
    q.adminSeeAll = !!adminSeeAll;

    if (followed) q.follow = true;

    if (priority.length && !priority.includes(TaskImportance.ALL)) {
      q.importance = priority; // send array
    }

    if (parentLevel && parentLevel !== TaskParent.ALL) {
      q.parentLevel =
        parentLevel === TaskParent.Parent_Tasks
          ? "Parent"
          : parentLevel === TaskParent.Sub_Tasks
          ? "Sub"
          : undefined;
    }

    if (status.length && !status.includes(TaskStatus.ALL)) {
      q.status = status; // send array
    }

    // Visibility (multi) — keep as ARRAY for the API
    if (visibility.length) {
      q.visibility = visibility;
    }

    // Multi
    if (selectedCreators.length)
      q.createdByIds = selectedCreators.map((u) => u.id);
    if (selectedAssignees.length)
      q.assignedUserIds = selectedAssignees.map((u) => u.id);
    if (selectedDepartments.length)
      q.departmentIds = selectedDepartments.map((d) => d.id);
    if (selectedProjects.length)
      q.projectIds = selectedProjects.map((p) => p.id);

    // Standard offset pagination
    q.page = page;
    q.limit = rowsPerPage;

    // Debug logging for query parameters
    console.log('Task Query Parameters:', {
      ...q,
      // Don't log the full query object to avoid clutter, just key params
      adminSeeAll: q.adminSeeAll,
      createdByIds: q.createdByIds?.length || 0,
      assignedUserIds: q.assignedUserIds?.length || 0,
      departmentIds: q.departmentIds?.length || 0,
      projectIds: q.projectIds?.length || 0
    });

    return q;
  }, [
    debouncedSearch,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    isPublic,
    followed,
    priority,
    parentLevel,
    status,
    visibility,
    selectedCreators,
    selectedAssignees,
    selectedDepartments,
    selectedProjects,
    page,
    rowsPerPage,
    adminSeeAll,
  ]);

  /* ----------------------------- URL sync (share) -------------------------- */
  const queryToSearchParams = (q: any) => {
    const sp = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
      if (v === undefined) return;
      if (Array.isArray(v)) {
        if (v.length) sp.set(k, v.join(",")); // serialize for share links
      } else {
        sp.set(k, String(v));
      }
    });
    return sp;
  };

  useEffect(() => {
    const sp = queryToSearchParams(query);
    setSearchParams(sp, { replace: true });
  }, [query, setSearchParams]);

  // Auto-save all filters
  useEffect(() => {
    const toSave = {
      title: searchValue,
      startDateFrom: startDateFrom?.toISOString() || null,
      startDateTo: startDateTo?.toISOString() || null,
      endDateFrom: endDateFrom?.toISOString() || null,
      endDateTo: endDateTo?.toISOString() || null,
      isPublic,
      follow: followed,
      importance: priority,
      parentLevel,
      status,
      visibility, // save visibility
      createdByIds: selectedCreators.map((u) => u.id),
      createdByUsernames: selectedCreators.map((u) => u.username),
      assignedUserIds: selectedAssignees.map((u) => u.id),
      assignedUsernames: selectedAssignees.map((u) => u.username),
      departmentIds: selectedDepartments.map((d) => d.id),
      departmentNames: selectedDepartments.map((d) => d.name),
      projectIds: selectedProjects.map((p) => p.id),
      projectNames: selectedProjects.map((p) => p.name),
      page,
      limit: rowsPerPage,
      adminSeeAll, // ← add this
    };
    localStorage.setItem("Home_filter", JSON.stringify(toSave));
  }, [
    searchValue,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    isPublic,
    followed,
    priority,
    parentLevel,
    status,
    visibility,
    selectedCreators,
    selectedAssignees,
    selectedDepartments,
    selectedProjects,
    page,
    rowsPerPage,
  ]);

  // Persist autoRefresh preference (NEW)
  useEffect(() => {
    localStorage.setItem("tasks:autoRefresh", String(autoRefresh));
  }, [autoRefresh]);

  // Reset to first page after typing a new search (NEW requirement #2)
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  /* ------------------------- Enhanced fetch with cancellation ----------------------- */
  const controllerRef = useRef<AbortController | null>(null);
  const reqSeqRef = useRef(0);
  const cacheRef = useRef<Map<string, { data: any; timestamp: number }>>(
    new Map()
  );

  const fetchTasks = useCallback(
    async (forceRefresh = false) => {
      const token = localStorage.getItem("accessToken");
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const mySeq = ++reqSeqRef.current;

      // Create cache key
      const cacheKey = JSON.stringify(query);
      const cached = cacheRef.current.get(cacheKey);
      const now = Date.now();
      const CACHE_DURATION = 30000; // 30 seconds

    // Use cache if available and not expired
    if (!forceRefresh && cached && now - cached.timestamp < CACHE_DURATION) {
      setRows(cached.data.tasks || []);
      setTotalRows(cached.data.totalCount || cached.data.pagination?.totalCount || 0);
      return;
    }

      setIsLoading(true);
      try {
        // clone current query
        const paramsForApi: any = { ...query };

        // 👉 Ensure multi-valued params are ARRAYS (not CSV)
        const ensureArray = (v: any) =>
          Array.isArray(v) ? v : v == null ? [] : [].concat(v);

        paramsForApi.createdByIds = ensureArray(query.createdByIds);
        paramsForApi.assignedUserIds = ensureArray(query.assignedUserIds);
        paramsForApi.departmentIds = ensureArray(query.departmentIds);
        paramsForApi.projectIds = ensureArray(query.projectIds);
        paramsForApi.visibility = ensureArray(query.visibility);

        const { data } = await axios.get(`${baseUrl}/tasks`, {
          params: paramsForApi,
          // repeat keys for arrays
          paramsSerializer: (params) => {
            const sp = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
              if (v === undefined || v === null) return;
              if (Array.isArray(v))
                v.forEach((val) => sp.append(k, String(val)));
              else sp.set(k, String(v));
            });
            return sp.toString();
          },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (mySeq !== reqSeqRef.current) return;

      // Update state with transition for better UX
      startTransition(() => {
        setRows(data.tasks || []);
        setTotalRows(data.totalCount || data.pagination?.totalCount || 0);
      });

        // Cache the result
        cacheRef.current.set(cacheKey, { data, timestamp: now });

        // Clean old cache entries
        if (cacheRef.current.size > 10) {
          const entries = Array.from(cacheRef.current.entries());
          entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
          cacheRef.current.clear();
          entries.slice(0, 5).forEach(([key, value]) => {
            cacheRef.current.set(key, value);
          });
        }
      } catch (error: any) {
        if (axios.isCancel?.(error) || error?.name === "CanceledError") {
          // ignore
        } else {
          console.error("Fetch tasks error:", error);
          setSnackbar({
            open: true,
            message: `Failed to fetch tasks: ${
              error?.response?.data?.message || error.message || "Unknown error"
            }`,
            severity: "error",
          });
        }
      } finally {
        if (mySeq === reqSeqRef.current) setIsLoading(false);
      }
    },
    [query]
  );

  useEffect(() => {
    fetchTasks();
    return () => controllerRef.current?.abort();
  }, [fetchTasks]);

  // Auto refresh interval (60s) – respects the chip toggle (requirement #1)
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchTasks();
    }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchTasks]);

  /* -------------------------------- Handlers ------------------------------- */
  const handleEdit = (taskId: number) => navigate(`/edit-task/${taskId}`);
  const handleAttach = (_taskId: number) => {
    // TODO: Implement attachment functionality
  };
  const handleComment = (taskId: number) => setActiveCommentTaskId(taskId);

  const handlePrivacy = async (id: number, privacy: boolean) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { public: privacy },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Task: ${
          error?.response?.data?.message || "Unknown error"
        }`,
        severity: "error",
      });
    }
  };

  const handleFollowToggle = async (taskid: number, isFollowed: boolean) => {
    try {
      const token = localStorage.getItem("accessToken");
      const endpoint = isFollowed ? "unfollow" : "follow";
      await axios.post(
        `${baseUrl}/tasks/${endpoint}`,
        { taskId: Number(taskid) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Follow toggle error:', error);
    } finally {
      fetchTasks();
    }
  };

  const saveFilters = () => {
    setSnackbar({ open: true, message: "Filters Saved", severity: "success" });
  };

  const clearFilters = () => {
    setSearchValue("");
    setSelectedAssignees([]);
    setSelectedCreators([]);
    setStartDateFrom(null);
    setStartDateTo(null);
    setEndDateFrom(null);
    setEndDateTo(null);
    setSelectedDepartments([]);
    setSelectedProjects([]);
    setStatus([TaskStatus.ALL]);
    setPriority([TaskImportance.ALL]);
    setParentLevel(TaskParent.ALL);
    setVisibility([]); // reset visibility
    setFollowed(false);
    setIsPublic(false);
    setCreatedByOthers(false); // ensure OFF
    setPage(1);
    setAdminSeeAll(isAdmin); // Reset to admin default (true for admins, false for non-admins)
  };

  // NEW: Apply "@defaults" quick filter (requirement #5)
  const applyDefaults = () => {
    clearFilters();
    // status: TODO, IN_PROGRESS, POSTPONED (no ALL)
    setStatus([TaskStatus.TODO, TaskStatus.IN_PROGRESS, TaskStatus.POSTPONED]);
    // assignee: me
    setSelectedAssignees([
      { id: currentUserId, username: currentUsername, imageUrl: null },
    ]);
    setPage(1);
  };

  // Standard pagination handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  // normalize multi with "ALL"
  const normalizeWithAll = (
    valueArr: string[],
    ALL: string,
    allValues: string[]
  ) => {
    let v = [...valueArr];
    if (!v.length) return [ALL];
    const hasAll = v.includes(ALL);
    if (hasAll && v.length > 1) v = v.filter((x) => x !== ALL);
    if (!hasAll && allValues.every((x) => v.includes(x))) return [...v];
    return v;
  };

  // Quick chips
  const isOverdueActive = useMemo(() => {
    if (!endDateTo || endDateFrom) return false;
    // consider "today end-of-day" as the marker for overdue chip
    return endDateTo.endOf("day").isSame(dayjs().endOf("day"));
  }, [endDateTo, endDateFrom]);

  const isDueThisWeekActive = useMemo(() => {
    if (!endDateTo) return false;
    const weekStart = dayjs().startOf("week");
    const weekEnd = dayjs().endOf("week");
    return (
      (!endDateFrom || endDateFrom.startOf("day").isSame(weekStart, "day")) &&
      endDateTo.endOf("day").isSame(weekEnd, "day")
    );
  }, [endDateTo, endDateFrom]);

  const applyOverdue = () => {
    const now = dayjs();
    if (isOverdueActive) {
      setEndDateTo(null);
    } else {
      setEndDateFrom(null);
      setEndDateTo(now);
      // optional: exclude DONE
      setStatus((prev) =>
        prev.includes(TaskStatus.ALL)
          ? [
              TaskStatus.TODO,
              TaskStatus.IN_PROGRESS,
              TaskStatus.POSTPONED,
              TaskStatus.REVIEW,
            ]
          : prev.filter((s) => s !== TaskStatus.DONE)
      );
    }
    setPage(1);
  };

  const applyDueThisWeek = () => {
    const weekStart = dayjs().startOf("week");
    const weekEnd = dayjs().endOf("week");
    if (isDueThisWeekActive) {
      setEndDateFrom(null);
      setEndDateTo(null);
    } else {
      setEndDateFrom(weekStart);
      setEndDateTo(weekEnd);
    }
    setPage(1);
  };

  const toggleCreatedByMe = () => {
    const exists = selectedCreators.some((u) => u.id === currentUserId);
    if (exists) {
      setSelectedCreators((prev) => prev.filter((u) => u.id !== currentUserId));
    } else {
      setSelectedCreators((prev) => [
        ...prev,
        {
          id: currentUserId,
          username: currentUsername,
          imageUrl: null,
        },
      ]);
    }
    setPage(1);
  };

  const toggleAssignedToMe = () => {
    const exists = selectedAssignees.some((u) => u.id === currentUserId);
    if (exists) {
      setSelectedAssignees((prev) =>
        prev.filter((u) => u.id !== currentUserId)
      );
    } else {
      setSelectedAssignees((prev) => [
        ...prev,
        {
          id: currentUserId,
          username: currentUsername,
          imageUrl: null,
        },
      ]);
    }
    setPage(1);
  };

  // Share filters to clipboard
  const copyShareLink = async () => {
    const sp = queryToSearchParams(query);
    const url = `${window.location.origin}${
      window.location.pathname
    }?${sp.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      setSnackbar({
        open: true,
        message: "Link copied to clipboard",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Couldn’t copy the link",
        severity: "error",
      });
    }
  };

  // Export to Excel (requirement #6) — Arabic headers, visible rows
  // put this inside your Home component

  const handleExportAllExcel = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      // helper: make sure multi-valued params are arrays (same as in fetchTasks)
      const ensureArray = (v: any) =>
        Array.isArray(v) ? v : v == null ? [] : [].concat(v);

      // we'll fetch page by page until we have everything
      const perPage = 1000; // tune if your API allows
      let pageNo = 1;
      const allTasks: Task[] = [];

      while (true) {
        const paramsForApi: any = {
          ...query, // same filters the table is using
          page: pageNo, // override page
          limit: perPage, // override page size
        };

        // normalize arrays (mirrors fetchTasks)
        paramsForApi.createdByIds = ensureArray(paramsForApi.createdByIds);
        paramsForApi.assignedUserIds = ensureArray(
          paramsForApi.assignedUserIds
        );
        paramsForApi.departmentIds = ensureArray(paramsForApi.departmentIds);
        paramsForApi.projectIds = ensureArray(paramsForApi.projectIds);
        paramsForApi.visibility = ensureArray(paramsForApi.visibility);

        const { data } = await axios.get(`${baseUrl}/tasks`, {
          params: paramsForApi,
          paramsSerializer: (params) => {
            const sp = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
              if (v === undefined || v === null) return;
              if (Array.isArray(v))
                v.forEach((val) => sp.append(k, String(val)));
              else sp.set(k, String(v));
            });
            return sp.toString();
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        const batch: Task[] = data?.tasks ?? [];
        allTasks.push(...batch);

        const total = Number(data?.totalCount ?? 0);

        // stop when this was the last page
        if (batch.length < perPage || (total && allTasks.length >= total)) {
          break;
        }

        pageNo += 1;
      }

      // convert to plain rows for Excel
      const rowsForExcel = allTasks.map((t) => ({
        ID: t.id,
        Title: t.title,
        Status: t.status,
        Priority: t.importance,
        Project: (t as any).projectName ?? "",
        Department: (t as any).departmentName ?? "",
        Creator: (t as any).createdByUsername ?? "",
        Assignee: (t as any).assignedToUsername ?? "",
        StartDate: t.startDate
          ? new Date(t.startDate).toLocaleDateString()
          : "",
        EndDate: t.endDate ? new Date(t.endDate).toLocaleDateString() : "",
        Public: t.public ? "Yes" : "No",
        Reference: (t as any).reference ?? "",
        Description: (t as any).description ?? "",
      }));

      // dynamic import (xlsx)
      const mod: any = await import("xlsx");
      const XLSX = mod.default ?? mod;

      const ws = XLSX.utils.json_to_sheet(rowsForExcel);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tasks");

      // Writing .xlsx preserves Arabic correctly
      XLSX.writeFile(wb, "tasks_all.xlsx");
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Export failed. Please try again.",
        severity: "error",
      });
    }
  };

  // status/importance option lists
  const STATUS_OPTIONS = [
    TaskStatus.ALL,
    TaskStatus.TODO,
    TaskStatus.IN_PROGRESS,
    TaskStatus.DONE,
    TaskStatus.POSTPONED,
    TaskStatus.REVIEW,
  ];
  const IMPORTANCE_OPTIONS = [
    TaskImportance.ALL,
    TaskImportance.NOT_IMPORTANT,
    TaskImportance.IMPORTANT,
    TaskImportance.VERY_IMPORTANT,
  ];

  // summary chips for active filters
  const summaryChips = useMemo(() => {
    const chips: { label: string; onDelete?: () => void }[] = [];
    if (isAdmin && adminSeeAll) {
      chips.push({
        label: "Admin: see all",
        onDelete: () => setAdminSeeAll(false),
      });
    }

    if (searchValue.trim())
      chips.push({
        label: `Search: ${searchValue}`,
        onDelete: () => setSearchValue(""),
      });

    if (selectedDepartments.length)
      chips.push({
        label: `Departments: ${selectedDepartments
          .map((d) => d.name)
          .join(", ")}`,
        onDelete: () => setSelectedDepartments([]),
      });

    if (selectedProjects.length)
      chips.push({
        label: `Projects: ${selectedProjects.map((p) => p.name).join(", ")}`,
        onDelete: () => setSelectedProjects([]),
      });

    if (status.length && !status.includes(TaskStatus.ALL))
      chips.push({
        label: `Status: ${status.join(", ")}`,
        onDelete: () => setStatus([TaskStatus.TODO]),
      });
    if (priority.length && !priority.includes(TaskImportance.ALL))
      chips.push({
        label: `Importance: ${priority.join(", ")}`,
        onDelete: () => setPriority([TaskImportance.ALL]),
      });

    if (visibility.length)
      chips.push({
        label: `Visibility: ${visibility.join(", ")}`,
        onDelete: () => setVisibility([]),
      });

    if (parentLevel && parentLevel !== TaskParent.ALL)
      chips.push({
        label: `Level: ${parentLevel}`,
        onDelete: () => setParentLevel(TaskParent.ALL),
      });

    // Created by me (summary chip)
    const createdByMeActive = selectedCreators.some(
      (u) => u.id === currentUserId
    );

    if (createdByMeActive) {
      chips.push({
        label: "Created by me",
        onDelete: () => {
          // remove myself from the creators filter
          setSelectedCreators((prev) =>
            prev.filter((u) => u.id !== currentUserId)
          );
        },
      });
    }

    // count the actually selected "others"
    const othersSelectedCount = selectedCreators.filter(
      (u) => u.id !== currentUserId
    ).length;

    if (createdByOthers) {
      chips.push({
        label: `Created by others: ${othersSelectedCount}`,
        onDelete: () => {
          setCreatedByOthers(false);
          // also clear others from the active selection (keep me if present)
          setSelectedCreators((prev) =>
            prev.filter((u) => u.id === currentUserId)
          );
        },
      });
    }

    if (selectedAssignees.length)
      chips.push({
        label: `Assignees: ${selectedAssignees
          .map((u) => u.username)
          .join(", ")}`,
        onDelete: () => setSelectedAssignees([]),
      });

    if (isPublic)
      chips.push({ label: "Public only", onDelete: () => setIsPublic(false) });
    if (followed)
      chips.push({ label: "Followed", onDelete: () => setFollowed(false) });

    if (startDateFrom)
      chips.push({
        label: `Start ≥ ${startDateFrom.format("YYYY-MM-DD")}`,
        onDelete: () => setStartDateFrom(null),
      });
    if (startDateTo)
      chips.push({
        label: `Start ≤ ${startDateTo.format("YYYY-MM-DD")}`,
        onDelete: () => setStartDateTo(null),
      });
    if (endDateFrom)
      chips.push({
        label: `End ≥ ${endDateFrom.format("YYYY-MM-DD")}`,
        onDelete: () => setEndDateFrom(null),
      });
    if (endDateTo)
      chips.push({
        label: `End ≤ ${endDateTo.format("YYYY-MM-DD")}`,
        onDelete: () => setEndDateTo(null),
      });

    return chips;
  }, [
    searchValue,
    selectedDepartments,
    selectedProjects,
    status,
    priority,
    visibility,
    parentLevel,
    selectedCreators,
    selectedAssignees,
    isPublic,
    followed,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    createdByOthers,
    users,
    adminSeeAll,
  ]);

  // Scroll height for sections in Advanced Filter (requirement #3)

  /* --------------------------------- UI ------------------------------------ */
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
        p: 2.5,
        minHeight: "100vh",
        background: theme.palette.mode === "dark"
          ? `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 50%), radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`
          : `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 50%), radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.main, 0.03)} 0%, transparent 50%)`,
      }}
    >
    {/* Top actions row - Ultra Premium Design */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.25,
            p: { xs: 2, md: 3 },
            background: theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
              : `linear-gradient(135deg, ${alpha("#fff", 1)} 0%, ${alpha("#fafafa", 0.95)} 50%, ${alpha("#fff", 1)} 100%)`,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
            boxShadow: theme.palette.mode === "dark"
              ? `0 12px 48px ${alpha("#000", 0.5)}, 0 4px 16px ${alpha("#000", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`
              : `0 12px 48px ${alpha("#000", 0.1)}, 0 4px 16px ${alpha("#000", 0.05)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
            backdropFilter: "blur(24px) saturate(200%)",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.6)} 50%, transparent 100%)`,
              animation: "shimmer 3s infinite",
              "@keyframes shimmer": {
                "0%": { transform: "translateX(-100%)" },
                "100%": { transform: "translateX(100%)" },
              },
            },
          }}
        >
          <Button
            onClick={() => navigate("/new-task/0")}
            variant="contained"
            size="medium"
            startIcon={<CalendarMonthIcon sx={{ fontSize: 20 }} />}
            sx={{ 
              position: "relative",
              borderRadius: 3, 
              fontWeight: 800, 
              height: 42, 
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: theme.palette.mode === "dark"
                ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.5)}, 0 2px 8px ${alpha("#000", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                : `0 6px 24px ${alpha(theme.palette.primary.main, 0.4)}, 0 2px 8px ${alpha("#000", 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.8)}`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              textTransform: "none",
              letterSpacing: 0.5,
              fontSize: "0.95rem",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: `linear-gradient(90deg, transparent 0%, ${alpha("#fff", 0.3)} 50%, transparent 100%)`,
                transition: "left 0.6s ease",
              },
              "&:hover": {
                transform: "translateY(-3px) scale(1.03)",
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.95)} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: theme.palette.mode === "dark"
                  ? `0 12px 32px ${alpha(theme.palette.primary.main, 0.6)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}, inset 0 1px 0 ${alpha("#fff", 0.25)}`
                  : `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
                "&::before": {
                  left: "100%",
                },
              },
              "&:active": {
                transform: "translateY(-1px) scale(1.01)",
              },
            }}
          >
            Add Task
          </Button>

          <Tooltip title="Refresh (force reload)" arrow placement="bottom">
            <span>
              <IconButton
                onClick={() => fetchTasks(true)}
                size="medium"
                disabled={isLoading}
                sx={{
                  width: 40,
                  height: 40,
                  background: theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.18)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)} 0%, ${alpha(theme.palette.success.main, 0.06)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.success.main, 0.35)}`,
                  backdropFilter: "blur(8px)",
                  boxShadow: `0 3px 12px ${alpha(theme.palette.success.main, 0.2)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.28)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`,
                    transform: "scale(1.15) rotate(-360deg)",
                    border: `1.5px solid ${alpha(theme.palette.success.main, 0.6)}`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.success.main, 0.12)}`,
                  },
                  "&:active": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={18} sx={{ color: theme.palette.success.main }} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Clear Filters" arrow placement="bottom">
            <IconButton 
              onClick={clearFilters} 
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.18)} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${alpha(theme.palette.error.main, 0.06)} 100%)`,
                border: `1.5px solid ${alpha(theme.palette.error.main, 0.35)}`,
                backdropFilter: "blur(8px)",
                boxShadow: `0 3px 12px ${alpha(theme.palette.error.main, 0.2)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.28)} 0%, ${alpha(theme.palette.error.main, 0.15)} 100%)`,
                  transform: "scale(1.15) rotate(90deg)",
                  border: `1.5px solid ${alpha(theme.palette.error.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.error.main, 0.12)}`,
                },
              }}
            >
              <ClearIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Save Filters" arrow placement="bottom">
            <IconButton
              onClick={saveFilters}
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.18)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.06)} 100%)`,
                border: `1.5px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                backdropFilter: "blur(8px)",
                boxShadow: `0 3px 12px ${alpha(theme.palette.warning.main, 0.2)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.28)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`,
                  transform: "scale(1.15) translateY(-2px)",
                  border: `1.5px solid ${alpha(theme.palette.warning.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.warning.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.warning.main, 0.12)}`,
                },
              }}
            >
              <SaveIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy shareable filter link" arrow placement="bottom">
            <IconButton 
              onClick={copyShareLink} 
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.18)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)} 0%, ${alpha(theme.palette.info.main, 0.06)} 100%)`,
                border: `1.5px solid ${alpha(theme.palette.info.main, 0.35)}`,
                backdropFilter: "blur(8px)",
                boxShadow: `0 3px 12px ${alpha(theme.palette.info.main, 0.2)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.28)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`,
                  transform: "scale(1.15) rotate(5deg)",
                  border: `1.5px solid ${alpha(theme.palette.info.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.info.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.info.main, 0.12)}`,
                },
              }}
            >
              <LinkIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export to Excel (Arabic)" arrow placement="bottom">
            <IconButton 
              onClick={handleExportAllExcel} 
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
                border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                backdropFilter: "blur(8px)",
                boxShadow: `0 3px 12px ${alpha(theme.palette.secondary.main, 0.2)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.28)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                  transform: "scale(1.15) translateY(-3px)",
                  border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.secondary.main, 0.12)}`,
                },
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={filtersOpen ? "Hide Filters" : "Show Filters"} arrow placement="bottom">
            <Box sx={{ position: "relative" }}>
              <IconButton 
                onClick={() => setFiltersOpen(!filtersOpen)}
                size="medium"
                sx={{
                  width: 40,
                  height: 40,
                  background: filtersOpen
                    ? theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
                    : theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.08)
                      : alpha(theme.palette.text.primary, 0.05),
                  border: `1.5px solid ${filtersOpen ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.divider, 0.3)}`,
                  backdropFilter: "blur(8px)",
                  boxShadow: filtersOpen
                    ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`
                    : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
                    transform: "scale(1.15)",
                    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                {filtersOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                )}
              </IconButton>
              {summaryChips.length > 0 && !filtersOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                    border: `2px solid ${theme.palette.background.paper}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 900,
                      color: "white",
                      lineHeight: 1,
                    }}
                  >
                    {summaryChips.length}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>

          <Divider 
            flexItem 
            orientation="vertical" 
            sx={{ 
              mx: 0.75,
              borderColor: alpha(theme.palette.divider, 0.3),
            }} 
          />

          <Tooltip title="Table View" arrow placement="bottom">
            <IconButton
              onClick={() => setViewMode("table")}
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: viewMode === "table"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
                  : alpha(theme.palette.text.primary, 0.04),
                border: `1.5px solid ${viewMode === "table" ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.3)}`,
                backdropFilter: "blur(8px)",
                boxShadow: viewMode === "table"
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`
                  : "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: viewMode === "table"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.22)} 100%)`
                    : alpha(theme.palette.primary.main, 0.1),
                  transform: "scale(1.15) translateY(-2px)",
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <TableViewIcon 
                sx={{ 
                  fontSize: 20,
                  color: viewMode === "table" ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: "all 0.3s ease",
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Grid View" arrow placement="bottom">
            <IconButton
              onClick={() => setViewMode("grid")}
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: viewMode === "grid"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
                  : alpha(theme.palette.text.primary, 0.04),
                border: `1.5px solid ${viewMode === "grid" ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.3)}`,
                backdropFilter: "blur(8px)",
                boxShadow: viewMode === "grid"
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`
                  : "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: viewMode === "grid"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.22)} 100%)`
                    : alpha(theme.palette.primary.main, 0.1),
                  transform: "scale(1.15) translateY(-2px)",
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <GridViewIcon 
                sx={{ 
                  fontSize: 20,
                  color: viewMode === "grid" ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: "all 0.3s ease",
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Calendar View" arrow placement="bottom">
            <IconButton
              onClick={() => setViewMode("calendar")}
              size="medium"
              sx={{
                width: 40,
                height: 40,
                background: viewMode === "calendar"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
                  : alpha(theme.palette.text.primary, 0.04),
                border: `1.5px solid ${viewMode === "calendar" ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.3)}`,
                backdropFilter: "blur(8px)",
                boxShadow: viewMode === "calendar"
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 0.5px 0 ${alpha("#fff", 0.15)}`
                  : "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: viewMode === "calendar"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.22)} 100%)`
                    : alpha(theme.palette.primary.main, 0.1),
                  transform: "scale(1.15) translateY(-2px)",
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <EventIcon 
                sx={{ 
                  fontSize: 20,
                  color: viewMode === "calendar" ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: "all 0.3s ease",
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Density toggle - Enhanced */}
          <ToggleButtonGroup
            value={density}
            exclusive
            size="small"
            onChange={(_, v) => v && setDensity(v)}
            sx={{ 
              ml: 0.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              borderRadius: 1.5,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: 1.5,
                px: 1.5,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.18)} 100%)`,
                  },
                },
                "&:hover": {
                  background: alpha(theme.palette.text.primary, 0.05),
                },
              },
            }}
          >
            <ToggleButton value="compact">
              <DensitySmallIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="comfortable">
              <DensityMediumIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Box flex={1} />

          {isAdmin && (
            <Chip
              size="small"
              variant={adminSeeAll ? "filled" : "outlined"}
              color={adminSeeAll ? "primary" : "default"}
              label="Admin: see all"
              onClick={() => {
                setAdminSeeAll((prev) => !prev);
                setPage(1);
              }}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                height: 32,
                background: adminSeeAll 
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`
                  : "transparent",
                border: `1.5px solid ${alpha(theme.palette.primary.main, adminSeeAll ? 0.6 : 0.3)}`,
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: adminSeeAll
                    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                    : "none",
                },
              }}
            />
          )}

          {/* Auto refresh as CHIP - Enhanced */}
          <Chip
            size="small"
            icon={<AutorenewIcon fontSize="small" />}
            variant={autoRefresh ? "filled" : "outlined"}
            color={autoRefresh ? "success" : "default"}
            label={autoRefresh ? "Auto refresh: On" : "Auto refresh: Off"}
            onClick={() => setAutoRefresh((v) => !v)}
            sx={{ 
              fontWeight: 700,
              borderRadius: 2,
              height: 32,
              background: autoRefresh
                ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${alpha(theme.palette.success.dark, 0.9)} 100%)`
                : "transparent",
              border: `1.5px solid ${alpha(autoRefresh ? theme.palette.success.main : theme.palette.divider, autoRefresh ? 0.6 : 0.3)}`,
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: autoRefresh
                  ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                  : "none",
              },
            }}
          />

          {/* Advanced Search - Ultra Premium Design */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              width: { xs: "100%", sm: 380, md: 520 },
              mr: 1,
              px: 2.5,
              py: 1,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              border: `1.5px solid ${alpha(theme.palette.divider, 0.4)}`,
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                : `linear-gradient(135deg, ${alpha("#fff", 1)} 0%, ${alpha("#fafafa", 0.98)} 50%, ${alpha("#fff", 1)} 100%)`,
              backdropFilter: "blur(16px) saturate(200%)",
              boxShadow: theme.palette.mode === "dark"
                ? `0 6px 24px ${alpha("#000", 0.35)}, inset 0 1px 0 ${alpha("#fff", 0.08)}, inset 0 0 30px ${alpha("#fff", 0.02)}`
                : `0 6px 24px ${alpha("#000", 0.08)}, inset 0 1px 0 ${alpha("#fff", 0.95)}, inset 0 0 30px ${alpha("#000", 0.02)}`,
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "2px",
                background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
                transition: "left 0.8s ease",
              },
              "&:hover": {
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                boxShadow: theme.palette.mode === "dark"
                  ? `0 10px 40px ${alpha("#000", 0.45)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`
                  : `0 10px 40px ${alpha("#000", 0.12)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.08)}`,
                transform: "translateY(-2px) scale(1.01)",
                "&::before": {
                  left: "100%",
                },
              },
              "&:focus-within": {
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
                transform: "translateY(-3px) scale(1.02)",
              },
            }}
          >
            <SearchIcon sx={{ ml: 1, opacity: 0.7 }} />
            <InputBase
              inputRef={searchInputRef}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                // page reset already handled by your debounced effect
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") setPage(1);
              }}
              placeholder="Search title, description, ref, or comments…"
              sx={{ ml: 1, flex: 1, fontSize: 14 }}
            />

            {/* Shortcut hint when empty */}
            {!searchValue && (
              <Chip
                size="small"
                label={
                  /Mac|iPod|iPhone|iPad/.test(navigator.platform)
                    ? "⌘ K"
                    : "Ctrl K"
                }
                variant="outlined"
                sx={{
                  mr: 1,
                  height: 22,
                  borderRadius: 1,
                  fontSize: 12,
                  color: "text.secondary",
                  borderColor: "divider",
                }}
              />
            )}

            {/* Clear / Loading */}
            {isLoading ? (
              <CircularProgress size={18} sx={{ mr: 1 }} />
            ) : searchValue ? (
              <Tooltip title="Clear">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchValue("");
                    setPage(1);
                    requestAnimationFrame(() =>
                      searchInputRef.current?.focus()
                    );
                  }}
                  sx={{ mr: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Button
              onClick={() => setPage(1)}
              size="small"
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{ 
                borderRadius: 999, 
                textTransform: "none", 
                mr: 0.5,
                fontWeight: 700,
                height: 32,
                px: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": {
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Search
            </Button>
          </Paper>

          {/* Filter button - Enhanced */}
          <Button
            variant="outlined"
            size="medium"
            startIcon={<TuneIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{ 
              textTransform: "none",
              borderRadius: 2.5,
              fontWeight: 700,
              height: 38,
              px: 2.5,
              border: `1.5px solid ${alpha(theme.palette.divider, 0.4)}`,
              background: theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.03)
                : alpha("#fff", 0.8),
              backdropFilter: "blur(8px)",
              transition: "all 0.3s ease",
              letterSpacing: 0.3,
              "&:hover": {
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                background: alpha(theme.palette.primary.main, 0.08),
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px ${alpha("#000", 0.1)}`,
              },
            }}
          >
            Advanced Filters
          </Button>

          {/* Results summary chip - Enhanced */}
          <Chip
            size="small"
            variant="outlined"
            label={`${totalRows.toLocaleString()} result${
              totalRows === 1 ? "" : "s"
            }`}
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              height: 32,
              background: theme.palette.mode === "dark"
                ? alpha(theme.palette.info.main, 0.08)
                : alpha(theme.palette.info.main, 0.05),
              border: `1.5px solid ${alpha(theme.palette.info.main, 0.3)}`,
              color: theme.palette.info.main,
            }}
          />
        </Box>

        {/* Quick Filters - Collapsible Drawer */}
        <Collapse in={filtersOpen} timeout={400}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            mt: 2,
            p: 2.5,
            background: theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.75)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 50%, ${alpha(theme.palette.background.paper, 0.75)} 100%)`
              : `linear-gradient(135deg, ${alpha("#fff", 0.95)} 0%, ${alpha("#fafafa", 0.8)} 50%, ${alpha("#fff", 0.95)} 100%)`,
            borderRadius: 3.5,
            border: `1.5px solid ${alpha(theme.palette.divider, 0.35)}`,
            backdropFilter: "blur(16px) saturate(180%)",
            boxShadow: theme.palette.mode === "dark"
              ? `0 8px 32px ${alpha("#000", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`
              : `0 8px 32px ${alpha("#000", 0.06)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0)} 0%, ${alpha(theme.palette.primary.main, 0.4)} 50%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
            },
          }}
        >
          {/* Quick chips */}
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            alignItems="center"
          >
            {/* @defaults first (requirement #5) */}
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label="@defaults"
              onClick={applyDefaults}
              sx={{ 
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.45)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px)",
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.22)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.3)}, 0 0 0 4px ${alpha(theme.palette.secondary.main, 0.1)}`,
                },
                "&:active": {
                  transform: "scale(1.02)",
                },
              }}
            />

            <Chip
              size="small"
              variant={isOverdueActive ? "filled" : "outlined"}
              icon={<BoltIcon sx={{ fontSize: 18 }} />}
              label="Overdue"
              onClick={applyOverdue}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(isOverdueActive ? theme.palette.error.main : theme.palette.divider, isOverdueActive ? 0.6 : 0.4)}`,
                background: isOverdueActive
                  ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.04)} 100%)`,
                boxShadow: isOverdueActive
                  ? `0 3px 12px ${alpha(theme.palette.error.main, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                  : `0 2px 8px ${alpha(theme.palette.error.main, 0.1)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px) rotate(-1deg)",
                  background: isOverdueActive
                    ? `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.95)} 0%, ${theme.palette.error.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.main, 0.08)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.error.main, 0.7)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.error.main, 0.12)}`,
                },
              }}
            />
            <Chip
              size="small"
              variant={isDueThisWeekActive ? "filled" : "outlined"}
              icon={<EventIcon sx={{ fontSize: 18 }} />}
              label="Due this week"
              onClick={applyDueThisWeek}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(isDueThisWeekActive ? theme.palette.primary.main : theme.palette.divider, isDueThisWeekActive ? 0.6 : 0.4)}`,
                background: isDueThisWeekActive
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                boxShadow: isDueThisWeekActive
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                  : `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px) rotate(1deg)",
                  background: isDueThisWeekActive
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.95)} 0%, ${theme.palette.primary.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            />
            <Chip
              size="small"
              variant={
                selectedCreators.some((u) => u.id === currentUserId)
                  ? "filled"
                  : "outlined"
              }
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              label="Created by me"
              onClick={toggleCreatedByMe}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(selectedCreators.some((u) => u.id === currentUserId) ? theme.palette.primary.main : theme.palette.divider, selectedCreators.some((u) => u.id === currentUserId) ? 0.6 : 0.4)}`,
                background: selectedCreators.some((u) => u.id === currentUserId)
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                boxShadow: selectedCreators.some((u) => u.id === currentUserId)
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                  : `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px)",
                  background: selectedCreators.some((u) => u.id === currentUserId)
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.95)} 0%, ${theme.palette.primary.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            />
            <Chip
              size="small"
              variant={createdByOthers ? "filled" : "outlined"}
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              label="Created by others"
              onClick={() => {
                setPage(1);
                setCreatedByOthers((prev) => {
                  const next = !prev;
                  if (!next) {
                    setSelectedCreators((prevCreators) =>
                      prevCreators.filter((u) => u.id === currentUserId)
                    );
                  }
                  return next;
                });
              }}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(createdByOthers ? theme.palette.primary.main : theme.palette.divider, createdByOthers ? 0.6 : 0.4)}`,
                background: createdByOthers
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                boxShadow: createdByOthers
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                  : `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px)",
                  background: createdByOthers
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.95)} 0%, ${theme.palette.primary.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            />

            <Chip
              size="small"
              variant={
                selectedAssignees.some((u) => u.id === currentUserId)
                  ? "filled"
                  : "outlined"
              }
              icon={<AssignmentIndIcon sx={{ fontSize: 18 }} />}
              label="Assigned to me"
              onClick={toggleAssignedToMe}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                height: 32,
                px: 0.5,
                border: `1.5px solid ${alpha(selectedAssignees.some((u) => u.id === currentUserId) ? theme.palette.primary.main : theme.palette.divider, selectedAssignees.some((u) => u.id === currentUserId) ? 0.6 : 0.4)}`,
                background: selectedAssignees.some((u) => u.id === currentUserId)
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                boxShadow: selectedAssignees.some((u) => u.id === currentUserId)
                  ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`
                  : `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                letterSpacing: 0.3,
                "&:hover": {
                  transform: "scale(1.08) translateY(-2px)",
                  background: selectedAssignees.some((u) => u.id === currentUserId)
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.95)} 0%, ${theme.palette.primary.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            />
          </Stack>

          {/* Active filter summary chips - Enhanced */}
          {summaryChips.length > 0 && (
            <Box 
              sx={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: 0.75,
                p: 1.5,
                borderRadius: 2,
                background: theme.palette.mode === "dark"
                  ? alpha(theme.palette.warning.main, 0.05)
                  : alpha(theme.palette.warning.main, 0.03),
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              {summaryChips.map((c, i) => (
                <Chip
                  key={`${c.label}-${i}`}
                  label={c.label}
                  onDelete={c.onDelete}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 1.5,
                    height: 26,
                    border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    background: theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.05)
                      : alpha("#fff", 0.8),
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      background: alpha(theme.palette.error.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    },
                    "& .MuiChip-deleteIcon": {
                      color: theme.palette.text.secondary,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: theme.palette.error.main,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
        </Collapse>

        {/* Enhanced Content with transitions */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          <Paper
            elevation={1}
            sx={{
              width: "100%",
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: theme.palette.background.paper,
              position: "relative",
            }}
          >
            {/* Loading overlay */}
            {isLoading && (
              <Fade in={isLoading}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: theme.palette.mode === "dark" 
                      ? "rgba(0, 0, 0, 0.8)" 
                      : "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(2px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress 
                      size={40} 
                      sx={{ 
                        color: theme.palette.mode === "dark" 
                          ? theme.palette.primary.light 
                          : theme.palette.primary.main 
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 1, 
                        opacity: 0.7,
                        color: theme.palette.mode === "dark" 
                          ? theme.palette.text.primary 
                          : theme.palette.text.secondary
                      }}
                    >
                      Loading tasks...
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            )}

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Fade in={!isLoading} timeout={300}>
                <Box>
                  {viewMode === "table" ? (
                    <TasksTable
                      rows={rows}
                      isLoading={isLoading || isPending}
                      setOpen={setOpen}
                      setTaskId={setTaskId}
                      currentUserId={currentUserId}
                    />
                  ) : viewMode === "grid" ? (
                    <TasksGrid
                      rows={rows}
                      isLoading={isLoading || isPending}
                      handleComment={handleComment}
                      handlePrivacy={handlePrivacy}
                      handleFollow={handleFollowToggle}
                      handleEdit={handleEdit}
                      handleAttach={handleAttach}
                      currentUserId={currentUserId}
                      density={density}
                      onRefresh={fetchTasks}
                    />
                  ) : (
                    <TasksCalendar
                      rows={rows}
                      isLoading={isLoading || isPending}
                    />
                  )}
                </Box>
              </Fade>
            </Box>

            {/* Enhanced pagination with keyset support */}
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              <TablePagination
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 200]}
                sx={{
                  px: 1,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 52,
                  },
                }}
                labelRowsPerPage="Tasks per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </Box>
          </Paper>
        </Box>

        {/* ======================= Advanced Filter Dialog ======================= */}
        <Dialog
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          fullWidth
          maxWidth="lg"
          fullScreen={isXs} // ✅ small screens: full-screen dialog
          PaperProps={{
            sx: {
              height: isXs ? "100vh" : { xs: "92vh", md: "86vh" },
              display: "flex",
              flexDirection: "column",
              borderRadius: isXs ? 0 : 2,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1.5 }}>
            Advanced Filters
            <Typography variant="body2" color="text.secondary">
              Narrow down tasks by your departments, projects, people,
              visibility and dates.
            </Typography>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              pt: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1.5,
              overflowY: "auto",
            }}
          >
            {/* --- General --- */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 280, // gives each section a reasonable box
                maxHeight: 520, // cap height so dialog doesn’t grow forever
              }}
            >
              <CardHeader
                title="General"
                subheader="Search and task attributes"
              />
              <CardContent
                sx={{
                  pt: 0,
                  flex: 1, // <-- lets content take remaining height
                  overflowY: "auto", // <-- internal scroll
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        // page reset handled by useEffect on debouncedSearch
                      }}
                      label="Search in title / description / ref / comments"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{ color: theme.palette.text.secondary }}
                              fontSize="small"
                            />
                          </InputAdornment>
                        ),
                        endAdornment: searchValue ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchValue("")}
                              edge="end"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ minWidth: 64 }}>
                        Status
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        onClick={() => setStatus([TaskStatus.ALL])}
                      >
                        All
                      </Button>
                    </Stack>

                    <Autocomplete<string, true, false, false>
                      multiple
                      disableCloseOnSelect
                      options={STATUS_OPTIONS}
                      value={status}
                      onChange={(_, val) => {
                        const normalized = normalizeWithAll(
                          val,
                          TaskStatus.ALL,
                          STATUS_OPTIONS.filter((x) => x !== TaskStatus.ALL)
                        );
                        setStatus(normalized);
                        setPage(1);
                      }}
                      filterSelectedOptions
                      isOptionEqualToValue={(a, b) => a === b}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                      renderTags={(value, getTagProps) => (
                        <Box
                          sx={{
                            maxHeight: 72,
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              size="small"
                              label={option}
                            />
                          ))}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Status" size="small" />
                      )}
                      sx={{
                        "& .MuiAutocomplete-inputRoot": {
                          alignItems: "flex-start",
                        },
                        "& .MuiChip-root": { height: 22, fontSize: 12 },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ minWidth: 64 }}>
                        Importance
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        onClick={() => setPriority([TaskImportance.ALL])}
                      >
                        All
                      </Button>
                    </Stack>

                    <Autocomplete<string, true, false, false>
                      multiple
                      disableCloseOnSelect
                      options={IMPORTANCE_OPTIONS}
                      value={priority}
                      onChange={(_, val) => {
                        const normalized = normalizeWithAll(
                          val,
                          TaskImportance.ALL,
                          IMPORTANCE_OPTIONS.filter(
                            (x) => x !== TaskImportance.ALL
                          )
                        );
                        setPriority(normalized);
                        setPage(1);
                      }}
                      filterSelectedOptions
                      isOptionEqualToValue={(a, b) => a === b}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                      renderTags={(value, getTagProps) => (
                        <Box
                          sx={{
                            maxHeight: 72,
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              size="small"
                              label={option}
                            />
                          ))}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Importance"
                          size="small"
                        />
                      )}
                      sx={{
                        "& .MuiAutocomplete-inputRoot": {
                          alignItems: "flex-start",
                        },
                        "& .MuiChip-root": { height: 22, fontSize: 12 },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <Autocomplete<string, false, false, false>
                        options={[
                          TaskParent.ALL,
                          TaskParent.Parent_Tasks,
                          TaskParent.Sub_Tasks,
                        ]}
                        value={parentLevel}
                        onChange={(_, v) => {
                          setParentLevel(v || TaskParent.ALL);
                          setPage(1);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Task Level"
                            size="small"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Public/Followed */}
                  <Grid item xs={12} sm={6}>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={followed}
                            onChange={(_, v) => {
                              setFollowed(v);
                              setPage(1);
                            }}
                            size="small"
                          />
                        }
                        label="Followed"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isPublic}
                            onChange={(_, v) => {
                              setIsPublic(v);
                              setPage(1);
                            }}
                            size="small"
                          />
                        }
                        label="Public"
                      />
                    </FormGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete<string, true, false, false>
                      multiple
                      disableCloseOnSelect
                      options={VISIBILITY_OPTIONS as unknown as string[]}
                      value={visibility}
                      onChange={(_, val) => {
                        setVisibility(val);
                        setPage(1);
                      }}
                      filterSelectedOptions
                      isOptionEqualToValue={(a, b) => a === b}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                      renderTags={(value, getTagProps) => (
                        <Box
                          sx={{
                            maxHeight: 72,
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              size="small"
                              label={option}
                            />
                          ))}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Visibility"
                          size="small"
                        />
                      )}
                      sx={{
                        "& .MuiAutocomplete-inputRoot": {
                          alignItems: "flex-start",
                        },
                        "& .MuiChip-root": { height: 22, fontSize: 12 },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* --- Scope: Departments & Projects --- */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 280, // gives each section a reasonable box
                maxHeight: 520, // cap height so dialog doesn’t grow forever
              }}
            >
              <CardHeader
                title="Scope"
                subheader="Your departments and projects (limited to your assignments)"
              />
              <CardContent
                sx={{
                  pt: 0,
                  flex: 1, // <-- lets content take remaining height
                  overflowY: "auto", // <-- internal scroll
                }}
              >
                <Grid container spacing={1.5}>
                  {/* Departments (my-only) */}
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 90 }}>
                        Departments
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        onClick={() => setSelectedDepartments(departments)}
                        disabled={!departments.length}
                      >
                        All mine
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setSelectedDepartments([])}
                      >
                        Clear
                      </Button>
                    </Stack>
                    <Autocomplete<Department, true, false, false>
                      multiple
                      options={departments}
                      value={selectedDepartments}
                      onChange={(_, val) => {
                        setSelectedDepartments(val);
                        setPage(1);
                      }}
                      getOptionLabel={(o) => o.name}
                      filterSelectedOptions
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Departments (mine)"
                          size="small"
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      This list includes only departments inferred from your
                      assigned projects.
                    </Typography>
                  </Grid>

                  {/* Projects (my-only, filtered by selected departments) */}
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 90 }}>
                        Projects
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        onClick={() => setSelectedProjects(projects)}
                        disabled={!projects.length}
                      >
                        All visible
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setSelectedProjects([])}
                      >
                        Clear
                      </Button>
                    </Stack>
                    <Autocomplete<Project, true, false, false>
                      multiple
                      options={projects}
                      value={selectedProjects}
                      onChange={(_, v) => {
                        setSelectedProjects(v);
                        setPage(1);
                      }}
                      getOptionLabel={(o) => o.name}
                      filterSelectedOptions
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Projects (yours)"
                          size="small"
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      No department selected → shows all your projects. If
                      department(s) are chosen, only your projects in those
                      department(s) are shown.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* --- People: Assignees & Creators --- */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 280, // gives each section a reasonable box
                maxHeight: 520, // cap height so dialog doesn’t grow forever
              }}
            >
              <CardHeader
                title="People"
                subheader="Users assigned in the selected project(s) (or all your projects if none selected)"
              />
              <CardContent
                sx={{
                  pt: 0,
                  flex: 1, // <-- lets content take remaining height
                  overflowY: "auto", // <-- internal scroll
                }}
              >
                <Grid container spacing={1.5}>
                  {/* Assignees */}
                  <Grid item xs={12}>
                    {fetchingUsers ? (
                      <CircularProgress sx={{ mt: 1 }} size={18} />
                    ) : (
                      <Autocomplete<User, true, false, false>
                        multiple
                        disableCloseOnSelect
                        options={users}
                        value={selectedAssignees}
                        onChange={(_, val) => {
                          setSelectedAssignees(val);
                          setPage(1);
                        }}
                        getOptionLabel={(u) => u?.username ?? ""}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        filterSelectedOptions
                        ListboxProps={{
                          style: { maxHeight: 320, overflowY: "auto" },
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={option.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Avatar
                              src={option.imageUrl || undefined}
                              sx={{ width: 22, height: 22 }}
                            >
                              {(option.username ?? "?").charAt(0)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={option.username ?? ""}
                              >
                                {option.username ?? ""}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) => (
                          <Box
                            sx={{
                              maxHeight: 72,
                              overflowY: "auto",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {value.map((u, i) => (
                              <Tooltip
                                key={u.id}
                                title={u.username ?? ""}
                                arrow
                              >
                                <Chip
                                  {...getTagProps({ index: i })}
                                  size="small"
                                  label={u.username ?? String(u.id)}
                                  avatar={
                                    <Avatar
                                      src={u.imageUrl || undefined}
                                      sx={{ width: 20, height: 20 }}
                                    >
                                      {(u.username ?? "?").charAt(0)}
                                    </Avatar>
                                  }
                                  sx={{
                                    maxWidth: 180,
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Assignees"
                            size="small"
                          />
                        )}
                      />
                    )}
                  </Grid>

                  {/* Created By */}
                  <Grid item xs={12}>
                    {fetchingUsers ? (
                      <CircularProgress sx={{ mt: 1 }} size={18} />
                    ) : (
                      <Autocomplete<User, true, false, false>
                        multiple
                        disableCloseOnSelect
                        options={users}
                        value={selectedCreators}
                        onChange={(_, val) => {
                          setSelectedCreators(val);
                          setPage(1);
                        }}
                        getOptionLabel={(u) => u?.username ?? ""}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        filterSelectedOptions
                        ListboxProps={{
                          style: { maxHeight: 320, overflowY: "auto" },
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={option.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Avatar
                              src={option.imageUrl || undefined}
                              sx={{ width: 22, height: 22 }}
                            >
                              {(option.username ?? "?").charAt(0)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={option.username ?? ""}
                              >
                                {option.username ?? ""}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) => (
                          <Box
                            sx={{
                              maxHeight: 72,
                              overflowY: "auto",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {value.map((u, i) => (
                              <Tooltip
                                key={u.id}
                                title={u.username ?? ""}
                                arrow
                              >
                                <Chip
                                  {...getTagProps({ index: i })}
                                  size="small"
                                  label={u.username ?? String(u.id)}
                                  avatar={
                                    <Avatar
                                      src={u.imageUrl || undefined}
                                      sx={{ width: 20, height: 20 }}
                                    >
                                      {(u.username ?? "?").charAt(0)}
                                    </Avatar>
                                  }
                                  sx={{
                                    maxWidth: 180,
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Created By"
                            size="small"
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* --- Dates --- */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 280, // gives each section a reasonable box
                maxHeight: 520, // cap height so dialog doesn’t grow forever
              }}
            >
              <CardHeader
                title="Dates"
                subheader="Filter by start/end date ranges"
              />
              <CardContent
                sx={{
                  pt: 0,
                  flex: 1, // <-- lets content take remaining height
                  overflowY: "auto", // <-- internal scroll
                }}
              >
                <Grid container spacing={1.5}>
                  {/* Start Date Range */}
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      Start Date
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      {/* Start: From */}
                      <Box sx={{ width: { xs: "100%", sm: 240 } }}>
                        <DatePicker
                          label="From"
                          value={startDateFrom}
                          onChange={(v) => {
                            setStartDateFrom(v);
                            setPage(1);
                          }}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                            },
                            actionBar: { actions: ["clear", "today"] },
                          }}
                        />
                      </Box>

                      {/* Start: To */}
                      <Box sx={{ width: { xs: "100%", sm: 240 } }}>
                        <DatePicker
                          label="To"
                          value={startDateTo}
                          onChange={(v) => {
                            setStartDateTo(v);
                            setPage(1);
                          }}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                            },
                            actionBar: { actions: ["clear", "today"] },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  {/* End Date Range */}
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      End Date
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      {/* End: From */}
                      <Box sx={{ width: { xs: "100%", sm: 240 } }}>
                        <DatePicker
                          label="From"
                          value={endDateFrom}
                          onChange={(v) => {
                            setEndDateFrom(v);
                            setPage(1);
                          }}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                            actionBar: { actions: ["clear", "today"] },
                          }}
                        />
                      </Box>

                      {/* End: To */}
                      <Box sx={{ width: { xs: "100%", sm: 240 } }}>
                        <DatePicker
                          label="To"
                          value={endDateTo}
                          onChange={(v) => {
                            setEndDateTo(v);
                            setPage(1);
                          }}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                            actionBar: { actions: ["clear", "today"] },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>

          {/* Sticky footer actions */}
          <DialogActions
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 1,
            }}
          >
            <Button
              onClick={clearFilters}
              color="inherit"
              startIcon={<ClearIcon />}
            >
              Reset
            </Button>
            <Box flex={1} />
            <Button onClick={() => setFilterOpen(false)}>Close</Button>
            <Button
              onClick={() => setFilterOpen(false)}
              variant="contained"
              startIcon={<FilterListIcon />}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Status Dialog with Comment */}
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
            setNewStatus(undefined);
            setTaskId(undefined);
            setStatusChangeComment("");
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Change My Task Status
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <Autocomplete<TaskStatus | "", false, false, false>
                options={[
                  TaskStatus.TODO,
                  TaskStatus.IN_PROGRESS,
                  TaskStatus.DONE,
                  TaskStatus.POSTPONED,
                ]}
                value={(newStatus as TaskStatus) || ""}
                onChange={(_, v) =>
                  setNewStatus((v as TaskStatus) || undefined)
                }
                renderInput={(params) => (
                  <TextField {...params} label="New Status" size="small" />
                )}
              />
            </FormControl>
            <DialogContentText sx={{ mb: 2, color: "text.secondary" }}>
              Add a comment or notes about this status change (optional):
            </DialogContentText>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              placeholder="Enter your comment or notes..."
              value={statusChangeComment}
              onChange={(e) => setStatusChangeComment(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            {newStatus && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main, display: "block", mb: 0.5 }}>
                  New Status:
                </Typography>
                <Chip
                  label={getUserTaskStatusDisplay(newStatus).label}
                  size="small"
                  icon={getUserTaskStatusDisplay(newStatus).icon}
                  sx={{
                    backgroundColor: alpha(getUserTaskStatusDisplay(newStatus).color, 0.15),
                    color: getUserTaskStatusDisplay(newStatus).color,
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setOpen(false);
                setNewStatus(undefined);
                setTaskId(undefined);
                setStatusChangeComment("");
              }}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setChangingStatus(true);
                try {
                  const token = localStorage.getItem("accessToken");
                  await axios.patch(
                    `${baseUrl}/tasks/usertask-status/${taskId}/${currentUserId}`,
                    { 
                      status: newStatus,
                      comment: statusChangeComment || undefined,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setSnackbar({
                    open: true,
                    message: "My task status updated successfully!",
                    severity: "success",
                  });
                  setOpen(false);
                  setNewStatus(undefined);
                  setTaskId(undefined);
                  setStatusChangeComment("");
                  fetchTasks();
                } catch {
                  setSnackbar({
                    open: true,
                    message: "Failed to update my task status. Please try again.",
                    severity: "error",
                  });
                } finally {
                  setChangingStatus(false);
                }
              }}
              disabled={changingStatus || !newStatus}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }}
            >
              {changingStatus ? <CircularProgress size={20} /> : "Save Status"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Comments */}
        {activeCommentTaskId !== null && (
          <CommentDialog
            open={true}
            onClose={() => setActiveCommentTaskId(null)}
            taskId={activeCommentTaskId}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Home;
